// The Instagram carousel preview (round 2 item 2). Pure, so the fidelity
// rules are asserted without jsdom.
//
// ⚠️⚠️ ALL SLIDES SHARE ONE ASPECT RATIO — THE FIRST ITEM'S. That is what
// Instagram does: the first photo sets the crop for the whole carousel, and
// every later slide is cropped to it. A preview that let each slide keep its
// own ratio would show the client a post that cannot publish. This is the
// detail that makes the carousel preview worth having.
//
// Instagram also clamps a feed ratio to between 4:5 portrait and 1.91:1
// landscape, so an extreme first photo is cropped to the nearest limit rather
// than shown as-is.
//
// Single media, a Reel or a Story keep their existing single preview.

import type { PostMedia } from '../../types/publishing';

export const IG_MIN_RATIO = 4 / 5; // tallest portrait Instagram allows
export const IG_MAX_RATIO = 1.91; // widest landscape
/** Before the first item's dimensions are known, Instagram's default square. */
export const IG_DEFAULT_RATIO = 1;

/** The carousel's one shared width/height ratio, from the FIRST item's dimensions. */
export function carouselAspectRatio(
  firstWidth: number | null | undefined,
  firstHeight: number | null | undefined,
): number {
  if (!firstWidth || !firstHeight || firstWidth <= 0 || firstHeight <= 0) return IG_DEFAULT_RATIO;
  const r = firstWidth / firstHeight;
  return Math.min(IG_MAX_RATIO, Math.max(IG_MIN_RATIO, r));
}

/** Does this post render as a carousel? Feed posts with two or more media. */
export function instagramCarouselApplies(
  media: readonly Pick<PostMedia, 'type'>[],
  contentType: 'feed' | 'reel' | 'story',
): boolean {
  return contentType === 'feed' && media.length >= 2;
}

/**
 * The slide in view, from the track's scroll position. Exposed so a later
 * feature (Instagram photo tagging addresses tags by media index) has an
 * active index to hang on.
 */
export function activeSlideIndex(scrollLeft: number, slideWidth: number, count: number): number {
  if (count <= 0 || slideWidth <= 0) return 0;
  return Math.min(count - 1, Math.max(0, Math.round(scrollLeft / slideWidth)));
}
